import { useEffect, useState } from 'react';
import { useUserSearch } from '@/lib/queries';
import type { UserSearchHit } from '@/lib/types';
import { Avatar } from '@/components/Avatar';
import { Modal } from '@/components/Modal';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  onPick: (user: UserSearchHit) => void;
  excludeOids?: string[];
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function UserPicker({
  open,
  title,
  onClose,
  onPick,
  excludeOids = [],
}: Props) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 200);
  const { data, isFetching } = useUserSearch(debounced);
  const exclude = new Set(excludeOids);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} ariaLabel={title} maxWidth="lg">
      <header className="border-b border-ink-800 px-6 py-4">
        <div className="label">Asignar manager</div>
        <h2 className="mt-1 text-lg font-light text-ink-50">{title}</h2>
      </header>
      <div className="border-b border-ink-800 px-6 py-3">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o email…"
          className="w-full border border-ink-800 bg-ink-900 px-3 py-2 text-sm text-ink-100 placeholder:text-ink-600 focus:border-ink-500 focus:outline-none"
        />
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        {debounced.trim().length < 2 ? (
          <div className="grid h-32 place-items-center text-xs text-ink-500">
            Escribe al menos 2 caracteres
          </div>
        ) : isFetching && !data ? (
          <div className="grid h-32 place-items-center text-xs text-ink-500">
            Buscando…
          </div>
        ) : !data?.items.length ? (
          <div className="grid h-32 place-items-center text-xs text-ink-500">
            Sin resultados
          </div>
        ) : (
          data.items.map((u) => {
            const already = exclude.has(u.oid);
            return (
              <button
                key={u.oid}
                type="button"
                disabled={already}
                onClick={() => onPick(u)}
                className={`flex w-full items-center gap-3 border-b border-ink-800/40 px-6 py-2.5 text-left transition-colors ${
                  already
                    ? 'cursor-not-allowed opacity-40'
                    : 'hover:bg-ink-900/60'
                }`}
              >
                <Avatar upn={u.upn} fullName={u.display_name} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-ink-100">{u.display_name}</div>
                  <div className="truncate text-xs text-ink-500">{u.upn}</div>
                </div>
                <div className="hidden text-right text-xs text-ink-400 sm:block">
                  {u.job_title ?? ''}
                </div>
                {already ? (
                  <span className="text-[0.7rem] uppercase tracking-widest text-ink-500">
                    ya asignado
                  </span>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
}
